import { createContext, useContext, useState, useRef, useCallback, useEffect, ReactNode } from "react";

interface TimerState {
  exerciseIndex: number;
  exerciseName: string;
  durationMin: number;
  elapsed: number;
  running: boolean;
  completed: boolean;
}

interface ExerciseTimerContextType {
  activeTimer: TimerState | null;
  startTimer: (index: number, name: string, durationMin: number) => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  resetTimer: () => void;
}

const ExerciseTimerContext = createContext<ExerciseTimerContextType | undefined>(undefined);

export const ExerciseTimerProvider = ({ children }: { children: ReactNode }) => {
  const [activeTimer, setActiveTimer] = useState<TimerState | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTickInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startTick = useCallback(() => {
    clearTickInterval();
    intervalRef.current = setInterval(() => {
      setActiveTimer((prev) => {
        if (!prev || !prev.running) return prev;
        const targetSec = prev.durationMin * 60;
        const newElapsed = prev.elapsed + 1;
        if (newElapsed >= targetSec) {
          clearTickInterval();
          return { ...prev, elapsed: targetSec, running: false, completed: true };
        }
        return { ...prev, elapsed: newElapsed };
      });
    }, 1000);
  }, []);

  const startTimer = useCallback((index: number, name: string, durationMin: number) => {
    clearTickInterval();
    const newState: TimerState = { exerciseIndex: index, exerciseName: name, durationMin, elapsed: 0, running: true, completed: false };
    setActiveTimer(newState);
  }, []);

  const pauseTimer = useCallback(() => {
    clearTickInterval();
    setActiveTimer((prev) => prev ? { ...prev, running: false } : null);
  }, []);

  const resumeTimer = useCallback(() => {
    setActiveTimer((prev) => prev && !prev.completed ? { ...prev, running: true } : prev);
  }, []);

  const resetTimer = useCallback(() => {
    clearTickInterval();
    setActiveTimer(null);
  }, []);

  // Start/stop interval based on running state
  useEffect(() => {
    if (activeTimer?.running) {
      startTick();
    } else {
      clearTickInterval();
    }
    return clearTickInterval;
  }, [activeTimer?.running, startTick]);

  return (
    <ExerciseTimerContext.Provider value={{ activeTimer, startTimer, pauseTimer, resumeTimer, resetTimer }}>
      {children}
    </ExerciseTimerContext.Provider>
  );
};

export const useExerciseTimer = () => {
  const context = useContext(ExerciseTimerContext);
  if (!context) throw new Error("useExerciseTimer must be used within ExerciseTimerProvider");
  return context;
};
